import type { Unset } from "@otto-ec/global-resources/event";
import type { CinemaItemState } from "./itemState.svelte";

export interface CinemaItemHTMLElement extends HTMLElement {
  /**
 *
 */
  isVisible?: boolean;
  /**
 *
 *
 */
  trackingIsVisible?: boolean;
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const useMainItemVisibilityObserver = (
  itemState: CinemaItemState,
  tick: () => Promise<void>,
  items: () => CinemaItemHTMLElement[],
  stage: () => HTMLElement,
  dispatchScrollEvent: () => unknown,
  setVisibleItems: (visibleItems: HTMLElement[]) => void,
  setStageScrollWidth: (width: number) => void,
  setStageScrolling: (scrolling: boolean) => void,
): [IntersectionObserver, Unset] => {
  let lastIntersectingItem: CinemaItemHTMLElement | null = null;

  const observer = new IntersectionObserver(
    async (entries) => {
      entries.forEach((entry) => {
        const target = entry.target as CinemaItemHTMLElement;
        target.isVisible = entry.isIntersecting;
        if (entry.isIntersecting) {
          lastIntersectingItem = target;
          setStageScrolling(true);
        }
      });

      /*                           */
      await tick();

      /*                                                           */
      setStageScrollWidth(stage().scrollWidth);
      const stageItems = items();

      let currentItemIndex = stageItems.findIndex((i) => i.isVisible);
      if (currentItemIndex === -1) {
        /*                                                            */
        currentItemIndex = stageItems.findIndex((i) => i === lastIntersectingItem);
      }
      itemState.currentItemIndex = currentItemIndex;

      /*                                                              */
      setVisibleItems(stageItems.filter((item) => item.isVisible));

      /*                                                                               */
      dispatchScrollEvent();
    },
    { root: stage(), threshold: [0.999, 1] },
  );

  items().forEach((i) => observer.observe(i));

  return [observer, () => observer.disconnect()] as const;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const useCustomItemVisibilityObserver = (
  tick: () => Promise<void>,
  items: () => CinemaItemHTMLElement[],
  stage: () => HTMLElement,
  dispatchScrollEvent: () => unknown,
  setVisibleItems: (visibleItems: HTMLElement[]) => void,
  itemVisibilityThreshold: number[] | undefined,
): [IntersectionObserver | null, Unset | null] => {
  if (!itemVisibilityThreshold) return [null, null] as const;

  const observer = new IntersectionObserver(
    async (entries) => {
      entries.forEach((entry) => {
        const target = entry.target as CinemaItemHTMLElement;
        target.trackingIsVisible = entry.isIntersecting;
      });

      await tick();

      setVisibleItems(items().filter((item) => item.trackingIsVisible));

      dispatchScrollEvent();
    },
    { root: stage(), threshold: itemVisibilityThreshold },
  );

  items().forEach((i) => observer.observe(i));

  return [observer, () => observer.disconnect()] as const;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const useStageMutationObserver = (
  Host: HTMLOcCinemaV1Element,
  setItems: (items: CinemaItemHTMLElement[]) => void,
  mainItemVisibilityObserver: IntersectionObserver,
  customItemVisibilityObserver: IntersectionObserver | null,
) => {
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ removedNodes, addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          mainItemVisibilityObserver.observe(node);
          customItemVisibilityObserver?.observe(node);
        }
      });

      removedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          mainItemVisibilityObserver.unobserve(node);
          customItemVisibilityObserver?.unobserve(node);
        }
      });

      setItems(Array.from(Host.children) as CinemaItemHTMLElement[]);
    });
  });

  mutationObserver.observe(Host, { childList: true });
  return () => mutationObserver.disconnect();
};
